import React, { FC, useMemo } from 'react'

import styled from 'styled-components'

import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import Waiting from 'src/Assets/Svg/common/waiting.svg?react'
import Empty from 'src/Assets/Svg/common/empty.svg?react'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
} from 'src/Types/campaigns'
import { CampaignPayment } from 'src/Redux/artistOrderApi/campaignPayment'
import { CampaignPaymentInfo } from 'src/Components/CampaignPaymentInfo'
import dayJs from 'src/Helpers/dayjs'
import { formatDate } from 'src/Constants/formatDate'
import { CTAButton } from 'src/Components/Buttons/CTAButton'

const MainInfoContainer = styled.div<{
  $approved?: boolean
  $compact?: boolean
}>`
  display: flex;
  ${({ $compact }) => !$compact && 'height: 400px;'}
  padding: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ $approved }) => ($approved ? '24px' : '16px')};
  border-radius: 16px;
  border: 1px solid
    ${({ theme, $approved }) =>
      $approved ? theme.color.brandGreen : theme.color.lightGrey};
  background-color: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  width: 100%;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    border: 1px solid
      ${({ theme, $approved }) =>
        $approved ? theme.color.brandGreen : 'transparent'};

    box-shadow: none;
  }
`

const InfoText = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize14};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
  max-width: 400px;
`

const TitleText = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize18};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const StyledCTAButton = styled(CTAButton)`
  padding: 0 40px;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

export type InfoBlockProps = {
  campaignStatus?: CampaignStatusEnum
  campaignId?: number
  payment?: CampaignPayment | null
  startDate?: string
  videosCount?: number
  onStartCampaignClick?: () => void
  compact?: boolean
}

const runningStatuses = [
  CampaignStatusEnum.RUNNING,
  CampaignStatusEnum.INPROCESS,
]

const stoppedStatuses = [
  CampaignStatusEnum.STOPPED,
  CampaignStatusEnum.CANCELED,
  CampaignStatusEnum.CANCELED_BY_ADMIN,
]

const getInfoBlockContent = (
  campaignStatus: CampaignStatusEnum | undefined,
  formattedStartDate: string,
  noVideos: boolean,
  onStartCampaignClick?: () => void,
): React.ReactNode => {
  if (campaignStatus === CampaignStatusEnum.AWAITING_REVIEW) {
    return (
      <>
        <Waiting />
        <InfoText>A&R team will review your track within 15-45 min.</InfoText>
      </>
    )
  }

  if (campaignStatus === CampaignStatusEnum.PENDING) {
    return (
      <>
        <TextWrapper>
          <TitleText>Approved to start</TitleText>
          <InfoText>
            Your track has been successfully reviewed by our team! Now let`s
            feature your track in videos to attract new listeners and a wider
            audience.
          </InfoText>
        </TextWrapper>
        <StyledCTAButton onClick={onStartCampaignClick}>
          Start Campaign
        </StyledCTAButton>
      </>
    )
  }

  if (campaignStatus === CampaignStatusEnum.SCHEDULED) {
    return (
      <>
        <Waiting />
        <InfoText>
          {`Sound will be available to creators on ${formattedStartDate}.`}
        </InfoText>
      </>
    )
  }

  if (campaignStatus && runningStatuses.includes(campaignStatus) && noVideos) {
    return (
      <>
        <Waiting />
        <InfoText>
          Videos are in progress.
          <br />
          Check back in 1–2 days to see the first results.
        </InfoText>
      </>
    )
  }

  if (campaignStatus && stoppedStatuses.includes(campaignStatus) && noVideos) {
    return (
      <>
        <Empty />
        <InfoText>
          You stopped the campaign before receiving any video from creators.
        </InfoText>
      </>
    )
  }

  return null
}

export const InfoBlock: FC<InfoBlockProps> = ({
  campaignStatus,
  campaignId,
  payment,
  startDate,
  videosCount,
  onStartCampaignClick,
  compact,
}) => {
  const approvedStatus = campaignStatus === CampaignStatusEnum.PENDING
  const formattedStartDate = dayJs(startDate).format(formatDate.formatDate1)
  const noVideos = videosCount === 0

  const content = useMemo(
    () =>
      getInfoBlockContent(
        campaignStatus,
        formattedStartDate,
        noVideos,
        onStartCampaignClick,
      ),
    [campaignStatus, formattedStartDate, noVideos, onStartCampaignClick],
  )

  if (payment && campaignId) {
    return (
      <MainInfoContainer $compact={compact}>
        <CampaignPaymentInfo
          payment={payment}
          platform={CampaignPlatformTypeEnum.TIKTOK}
          campaignId={campaignId}
        />
      </MainInfoContainer>
    )
  }

  if (!content) {
    return null
  }

  return (
    <MainInfoContainer $approved={approvedStatus} $compact={compact}>
      {content}
    </MainInfoContainer>
  )
}
